<template>
  <b-tabs
    id="event"
    :class="{
      'existing-event': event.id,
      'new-event': !event.id,
      'submitting': submitting,
      'registration-open': eventStatus(eventCopy).canRegister,
    }"
    v-if="eventLoaded"
    v-model="tabIndex"
    @input="updateRouteWithTab(event.id)"
    no-fade>

    <!-- Event Form -->
    <b-tab
      id="event-details"
      class="mt-3"
      title-link-class="event-details-tab"
      :title="`Event: ${eventCopy.name}`">
      <form
        class="container"
        @submit.prevent="submitEvent(event)"
        novalidate>
        <div class="row">
          <div class="col"><small class="text-danger font-italic">* Required fields</small></div>
        </div>

        <!-- Event Name / Season  -->
        <div class="row mt-3">
          <!-- Event Name  -->
          <div class="col-md-4">
            <label for="event-name">Event Name: <span class="text-danger font-italic">*</span></label>
            <b-form-input
              id="event-name"
              ref="eventName"
              name="eventName"
              type="text"
              placeholder="Enter an event name"
              v-model="event.name"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')">
            </b-form-input>
          </div>
          <!-- Event Name -->

          <!-- Season -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label for="event-season">Season: <span class="text-danger font-italic">*</span></label>
            <b-form-select
              id="event-season"
              ref="eventSeason"
              name="eventSeason"
              v-model="event.season_id"
              :disabled="!hasRole('superadmin') || submitting">
              <option selected :value="undefined">Select Season</option>
              <option
                v-for="s in seasons"
                :key="s.id"
                :value="s.id">
                {{s.name}}
              </option>
            </b-form-select>
          </div>
          <!-- / Season -->
        </div>
        <!-- Event Name / Season -->


        <!-- Event Start / End -->
        <div class="row mt-3">
          <!-- Event Start -->
          <div class="col-md-4">
            <label for="event-start">Event Start: <span class="text-danger font-italic">*</span></label>
            <DateTimePicker
              id="event-start"
              ref="eventStart"
              name="eventStart"
              v-model="event.start_date_time"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
            />
          </div>
          <!-- / Event Start -->

          <!-- Event End -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label for="event-end">Event End: <span class="text-danger font-italic">*</span></label>
            <DateTimePicker
              id="event-end"
              ref="eventEnd"
              name="eventEnd"
              v-model="event.end_date_time"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
            />
          </div>
          <!-- / Event End -->

          <!-- Private Event Checkbox -->
          <div class="col-md-4">
            <label class="d-none d-md-block">&nbsp;</label>
            <b-form-checkbox
              id="event-hidden"
              class="mt-3"
              name="eventHidden"
              ref="eventHidden"
              v-model="event.hidden"
              :readonly="submitting">
              Private Event
              <span
                v-b-tooltip.hover.right
                title="Private Events can only be
                accessed via the link at the
                bottom of this page. You
                may share this link as you see fit.">
                <font-awesome-icon
                  size="sm"
                  icon="info-circle">
                </font-awesome-icon>
              </span>
            </b-form-checkbox>
          </div>
          <!-- Private Event Checkbox -->
        </div>
        <!-- / Event Start / End -->

        <!-- Registration Open / Close / Max -->
        <div class="row mt-3">
          <!-- Registration Open -->
          <div class="col-md-4">
            <label for="event-registration-open">Registration Open: <span class="text-danger font-italic">*</span></label>
            <DateTimePicker
              id="event-registration-open"
              ref="eventRegistrationOpen"
              name="eventRegistrationOpen"
              v-model="event.registration_start_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Registration Open -->

          <!-- Registration Close -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label for="event-registration-close">Registration Close: <span class="text-danger font-italic">*</span></label>
            <DateTimePicker
              id="event-registration-close"
              ref="eventRegistrationClose"
              name="eventRegistrationClose"
              v-model="event.registration_end_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Registration Close-->

          <!-- Max Registrants -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label class="d-flex align-items-center" for="roster-max-registrants">
              Max Registrants:&nbsp;<span class="text-danger font-italic">*</span>
              <small class="ml-auto text-muted">Enter -1 for unlimited</small>
            </label>
            <Number
              id="roster-max-registrants"
              ref="rosterMaxRegistrants"
              name="rosterMaxRegistrants"
              type="number"
              step="1"
              min="-1"
              display-decimals="0"
              value-decimals="0"
              placeholder="Enter max active roster size"
              v-model="roster.max_registrations"
              :readonly="submitting"
            />
          </div>
          <!-- / Max Registrants -->
        </div>
        <!-- / Registration Open / Close -->

        <!-- Waitlist Open / Close / Max -->
        <div class="row mt-3">
          <!-- Waitlist Open -->
          <div class="col-md-4">
            <label for="event-waitlist-open">Waitlist Open:</label>
            <DateTimePicker
              id="event-waitlist-open"
              ref="eventWaitlistOpen"
              name="eventWaitlistOpen"
              v-model="event.waitlist_start_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Waitlist Open -->

          <!-- Waitlist Close -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label for="event-waitlist-close">Waitlist Close:</label>
            <DateTimePicker
              id="event-waitlist-close"
              ref="eventWaitlistClose"
              name="eventWaitlistClose"
              v-model="event.waitlist_end_date_time"
              :readonly="submitting"
            />
          </div>
          <!-- / Waitlist Close-->

          <!-- Max Waitlist -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label class="d-flex align-items-center" for="roster-max-waitlist">
              Max Waitlist:
              <small class="ml-auto text-muted">Enter -1 for unlimited</small>
            </label>
            <Number
              id="roster-max-waitlist"
              ref="rosterMaxWaitlist"
              name="rosterMaxWaitlist"
              type="number"
              step="1"
              min="-1"
              display-decimals="0"
              value-decimals="0"
              placeholder="Enter max waitlist size"
              v-model="roster.max_waitlist"
              :readonly="submitting"
            />
          </div>
          <!-- / Max Waitlist -->
        </div>
        <!-- / Waitlist Open / Close / Max -->

        <!-- Tagline / Contact Email / Phone -->
        <div class="row mt-3">
          <!-- Tagline  -->
          <div class="col-md">
            <label for="event-tagline">Event Tagline:</label>
            <b-form-input
              id="event-tagline"
              ref="eventTagline"
              name="eventTagline"
              type="text"
              placeholder="Enter an event tagline"
              v-model="event.long_name"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')">
            </b-form-input>
          </div>
          <!-- / Tagline  -->

          <!-- Contact Email -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label for="event-email">Contact Email: <span class="text-danger font-italic">*</span></label>
            <b-form-input
              id="event-email"
              ref="eventEmail"
              name="eventEmail"
              type="email"
              placeholder="Enter an email address"
              v-model="event.contact_email"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')">
            </b-form-input>
          </div>
          <!-- / Contact Email -->

          <!-- Contact Phone -->
          <div class="col-md-4 mt-3 mt-md-0">
            <label for="event-tagline">Contact Phone:</label>
            <b-form-input
              id="event-phone"
              ref="eventPhone"
              name="eventPhone"
              type="text"
              placeholder="Enter a phone number"
              v-model="event.contact_phone"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')">
            </b-form-input>
          </div>
          <!-- / Contact Phone  -->
        </div>
        <!-- / Tagline / Contact Email / Phone -->

        <!-- Location / Description -->
        <div class="row mt-3">
          <!-- Location -->
          <div class="col-md">
            <label for="event-location">Location:</label>
            <b-form-textarea
              id="event-location"
              ref="eventLocation"
              name="eventLocation"
              rows="6"
              placeholder="Enter an event location"
              v-model="event.location"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
              no-resize>
            </b-form-textarea>
          </div>
          <!-- / Location -->

          <!-- Description  -->
          <div class="col-md mt-3 mt-md-0">
            <label for="event-description">Description:</label>
            <b-form-textarea
              id="event-description"
              ref="eventDescription"
              name="eventDescription"
              rows="6"
              v-model="event.description"
              placeholder="Enter an event description"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
              no-resize>
            </b-form-textarea>
          </div>
          <!-- / Description  -->
        </div>
        <!-- / Location / Description -->

        <!-- Refund Policy / Contact Instructions -->
        <div class="row mt-3">
          <!-- Refund Policy  -->
          <div class="col-md">
            <label for="event-refund-policy">Refund Policy:</label>
            <b-form-textarea
              id="event-refund-policy"
              ref="eventRefundPolicy"
              name="eventRefundPolicy"
              rows="6"
              placeholder="Enter a refund policy for this event"
              v-model="event.refund_policy"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
              no-resize>
            </b-form-textarea>
          </div>
          <!-- / Refund Policy  -->

          <!-- Contact Instructions  -->
          <div class="col-md mt-3 mt-md-0">
            <label for="event-contact-instructions">Special Contact Instructions:</label>
            <b-form-textarea
              id="event-contact-instructions"
              ref="eventContactInstructions"
              name="eventContactInstructions"
              rows="6"
              v-model="event.contact_text"
              placeholder="Enter instructions on who to contact in case of a problem"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
              no-resize>
            </b-form-textarea>
          </div>
          <!-- / Contact Instructions  -->
        </div>
        <!-- / Refund Policy / Contact Instructions -->

        <!-- Compliance Statement -->
        <div class="row mt-3">
          <!-- Compliance Statement  -->
          <div class="col-md-6">
            <label for="event-compliance-statement">Compliance Statement:</label>
            <b-form-textarea
              id="event-compliance-statement"
              ref="eventComplianceStatement"
              name="eventComplianceStatement"
              rows="6"
              placeholder="Enter a compliance statement"
              v-model="event.compliance_statement"
              :readonly="submitting"
              :disabled="!hasRole('superadmin')"
              no-resize>
            </b-form-textarea>
          </div>
          <!-- / Compliance Statement  -->
        </div>
        <!-- / Compliance Statement -->

        <!-- Registraton Site Link -->
        <div
          v-if="event.id"
          class="row mt-3">
          <div class="col">
            <b-button
              :disabled="!eventStatus(eventCopy).canRegister"
              :href="registrationSiteURL"
              class="px-0"
              target="_blank"
              type="button"
              variant="link">
              Link To Register For {{eventCopy.hidden ? 'Private' : ''}} Event
              <span v-if="!eventStatus(eventCopy).canRegister">
                (Registration Closed)
              </span>
            </b-button>
          </div>
        </div>
        <!-- / Registraton Site Link -->

        <div class="corner-buttons">
          <!-- Save Event Button -->
          <b-button
            class="shadow"
            type="submit"
            variant="success"
            :disabled="submitting">
            <font-awesome-icon icon="save"></font-awesome-icon> Save
          </b-button>
          <!-- / Save Event Button -->

          <!-- Copy Event Button -->
          <b-button
            class="shadow ml-3"
            type="button"
            variant="success"
            v-if="event.id"
            :disabled="submitting"
            @click="copyEvent(event)">
            <font-awesome-icon icon="copy"></font-awesome-icon> Copy
          </b-button>
          <!-- / Copy Event Button -->

          <!-- Delete Event Button -->
          <b-button
            class="shadow ml-3"
            type="button"
            variant="danger"
            v-if="event.id"
            :disabled="submitting"
            @click="submitEvent(event.id)">
            <font-awesome-icon icon="trash"></font-awesome-icon> Delete Event
          </b-button>
          <!-- / Delete Event Button -->
        </div>
      </form>
    </b-tab>
    <!-- Event Form -->

    <!-- Fees & Pricing -->
    <b-tab
      class="mt-3"
      v-if="hasRole('superadmin') && event.id"
      :title="`Fees & Pricing (${pricing.length})`">
      <form
        id="event-fees-form"
        name="eventFeesForm"
        @submit.prevent="submitEvent(event)"
        novalidate>
        <!-- Fees -->
        <h4 class="container-fluid my-3 text-secondary">
          {{loggedInUser.actingOrg.name}} Fees<hr>
        </h4>

        <div class="container">
          <div class="row mb-3">
            <!-- Fee Percent -->
            <div class="col-md-3">
              <label for="event-fee-percent">
                Fee Percentage: <span class="text-danger font-italic">*</span>
              </label>
              <Number
                id="event-fee-percent"
                ref="eventFeePercent"
                name="eventFeePercent"
                type="percent"
                step="0.1"
                placeholder="Enter a fee percent"
                v-model="event.fees_percentage"
                :readonly="submitting"
              />
            </div>
            <!-- / Fee Percent -->

             <!-- Fee Flat -->
            <div class="col-md-3 mt-3 mt-md-0">
              <label for="event-fee-flat">
                Flat Fee:
              </label>
              <Number
                id="event-fee-flat"
                ref="eventFeeFlat"
                name="eventFeeFlat"
                type="currency"
                step="10"
                placeholder="Enter a flat fee"
                v-model="event.fees_flat"
                :readonly="submitting"
              />
            </div>
            <!-- / Fee Flat -->

            <!-- Fee Included -->
            <div class="col-md-3 mt-3 mt-md-2">
              <label class="d-none d-md-block">&nbsp;</label>
              <b-form-checkbox
                id="event-fee-included"
                class="mt-md-2"
                name="eventFeeIncluded"
                ref="eventFeeIncluded"
                v-model="event.fees_included"
                :value="false"
                :unchecked-value="true"
                :readonly="submitting">
                Pass fees onto registrant
              </b-form-checkbox>
            </div>
            <!-- / Fee Included -->
          </div>
        </div>
        <!-- / Fees -->

        <!-- KJK Fees -->
        <h4 class="container-fluid my-3 text-secondary">
          KJK Fees<hr>
        </h4>

        <div class="container">
          <div class="row mb-3">
            <!-- Fee Percent -->
            <div class="col-md-3">
              <label for="kjk-event-fee-percent">
                Fee Percentage:
              </label>
              <Number
                id="kjk-event-fee-percent"
                ref="eventFeePercent"
                name="eventFeePercent"
                type="percent"
                step="0.1"
                placeholder="Enter a fee percent"
                v-model="event.kjk_fees_percentage"
                :readonly="submitting"
              />
            </div>
            <!-- / Fee Percent -->

             <!-- Fee Flat -->
            <div class="col-md-3 mt-3 mt-md-0">
              <label for="kjk-event-fee-flat">
                Flat Fee:
              </label>
              <Number
                id="kjk-event-fee-flat"
                ref="eventFeeFlat"
                name="eventFeeFlat"
                type="currency"
                step="10"
                placeholder="Enter a flat fee"
                v-model="event.kjk_fees_flat"
                :readonly="submitting"
              />
            </div>
            <!-- / Fee Flat -->
          </div>
        </div>
        <!-- / KJK Fees -->

        <!-- Partial Payments -->
        <h4 class="container-fluid my-3 text-secondary">
          Partial Payments<hr>
        </h4>

        <div class="container">
          <div class="row mb-3">
            <!-- Allow Partial Payments -->
            <div class="col-md-3">
              <label class="d-none d-md-block">&nbsp;</label>
              <b-form-checkbox
                id="event-allow-partial-payments"
                class="mt-md-2"
                name="eventAllowPartialPayments"
                ref="eventAllowPartialPayments"
                v-model="event.partial_payments_enabled"
                @change.native="defaultPartialPaymentAmount(event.partial_payments_enabled)"
                :readonly="submitting">
                Allow Partial Payments
              </b-form-checkbox>
            </div>
            <!-- / Allow Partial Payments -->

            <!-- Partial Payment Minimum -->
            <div class="col-md-3 mt-3 mt-md-0">
              <label
                for="event-minimum-partial-payment"
                class="d-flex justify-content-between align-items-center">
                <div>
                  Min. Amount:
                  <span
                    v-b-tooltip.hover.right
                    title="Registrants' first partial payment will charge either
                    this amount or enough to cover KJK's fees, whichever
                    is higher. Subsequent payments can be any amount.">
                    <font-awesome-icon
                      size="sm"
                      icon="info-circle">
                    </font-awesome-icon>
                  </span>
                </div>
                <small v-if="event.partial_payments_enabled" class="text-danger ml-auto">required</small>
              </label>
              <Number
                id="event-minimum-partial-payment"
                ref="eventMinimumPartialPayment"
                name="eventMinimumPartialPayment"
                type="currency"
                step="10"
                placeholder="Enter a minimum amount for partial payment"
                v-model="event.partial_payments_minimum"
                :disabled="!event.partial_payments_enabled"
                :readonly="submitting"
              />
            </div>
            <!-- / Partial Payment Minimum -->

            <!-- Partial Payment Start -->
            <div class="col-md-3 mt-3 mt-md-0">
              <label for="event-partial-payment-start">Start:</label>
              <DateTimePicker
                id="event-partial-payment-start"
                ref="eventPartialPaymentStart"
                name="eventPartialPaymentStart"
                v-model="event.partial_payments_start_date_time"
                :readonly="submitting"
                :disabled="!event.partial_payments_enabled"
              />
            </div>
            <!-- / Partial Payment Start -->

            <!-- Partial Payment End -->
            <div class="col-md-3 mt-3 mt-md-0">
              <label for="event-partial-payment-end">End:</label>
              <DateTimePicker
                id="event-partial-payment-end"
                ref="eventPartialPaymentEnd"
                name="eventPartialPaymentEnd"
                v-model="event.partial_payments_end_date_time"
                :readonly="submitting"
                :disabled="!event.partial_payments_enabled"
              />
            </div>
            <!-- / Partial Payment End -->
          </div>
        </div>
        <!-- / Partial Payments -->

        <div class="corner-buttons">
          <!-- Save Event Button -->
          <b-button
            class="shadow"
            type="submit"
            variant="success"
            :disabled="submitting">
            <font-awesome-icon icon="save"></font-awesome-icon> Save
          </b-button>
          <!-- / Save Event Button -->
        </div>
      </form>

      <!-- Pricing -->
      <div class="container-fluid">
        <h4 class="my-3 text-secondary">
          <span>Pricing</span>
          <!-- Go To Pricing Package -->
          <b-button
            type="button"
            variant="link"
            size="sm"
            @click="goToPricing(event.id, 'new')">
            Add Pricing
          </b-button>
          <!-- / Go To Pricing Package -->
        </h4>

        <b-table
          striped
          hover
          selectable
          stacked="lg"
          small
          show-empty
          select-mode="single"
          sort-by="order"
          @row-selected="selectPricing"
          :items="pricing"
          :fields="pricingFields"
          empty-text="No Pricing to show.">
        </b-table>
      </div>
      <!-- / Pricing -->

      <!-- Promo Codes -->
      <div class="container-fluid">
        <h4 class="my-3 text-secondary">
          <span>Promo Codes</span>
          <!-- Go To Pricing Package -->
          <b-button
            type="button"
            variant="link"
            size="sm"
            @click="goToPromoCode(event.id, 'new')">
            Add Promo Code
          </b-button>
          <!-- / Go To Pricing Package -->
        </h4>

        <b-table
          striped
          hover
          selectable
          stacked="lg"
          small
          show-empty
          select-mode="single"
          @row-selected="selectPromoCode"
          :items="promoCodes"
          :fields="promoCodeFields"
          empty-text="No Promo Codes to show.">
          <!-- Valid From -->
          <template
            slot="valid_start_date_time"
            slot-scope="{ item }">
            {{item.display_valid_start_date_time}}
          </template>
          <!-- / Valid From -->

          <!-- Expires -->
          <template
            slot="valid_end_date_time"
            slot-scope="{ item }">
            {{item.display_valid_end_date_time}}
          </template>
          <!-- / Expires -->
        </b-table>
      </div>
      <!-- / Promo Codes -->
    </b-tab>
    <!-- / Fees & Pricing -->

    <!-- Registrants -->
    <b-tab
      id="event-registrants"
      class="mt-3 container-fluid"
      v-if="event.id"
      :title="`Registrants (${registrations.length})`">
      <!-- Active Roster -->
      <h4 class="my-3 d-flex align-items-center text-secondary">
        <span>Active Roster</span>
        <small class="ml-1">({{registrations.length}})</small>
        <!-- Go To Registration Form -->
        <b-button
          type="button"
          variant="link"
          size="sm"
          v-if="hasRole('superadmin')"
          @click="goToForm(event.id, eventForm.id)">
          {{ eventForm.form_fields.length ? 'Edit' : 'Create' }} Registration Form
        </b-button>
        <!-- / Go To Registration Form -->

        <!-- Message Registrants -->
        <b-button
          type="button"
          variant="link"
          size="sm"
          :disabled="registrations.length === 0"
          @click="messageEvents([event.id])">
          Message Active Roster
        </b-button>
        <!-- / Message Registrants -->
      </h4>

      <b-table
        striped
        hover
        stacked="lg"
        small
        show-empty
        sort-by="created_at"
        :items="registrations"
        :fields="activeRosterFields"
        empty-text="No Active Registrants to show.">
        <!-- Registrant Actions -->
        <template
          slot="actions"
          slot-scope="{ item }">
          <b-dropdown
            variant="link"
            boundary="window">
            <!-- Hamburger -->
            <template slot="button-content">
              <font-awesome-icon icon="bars"></font-awesome-icon>
            </template>
            <!-- / Hamburger -->

            <!-- Options -->
            <b-dropdown-item
              @click="goToOrder(item.registration_session_uuid)">
              Order Details
            </b-dropdown-item>

            <b-dropdown-item
              @click="goToRegistration(event.id, item.id)">
              Registration Details
            </b-dropdown-item>

            <b-dropdown-item
              @click="resendConfirmationEmail(item.registration_session_uuid)">
              Resend Confirmation Email
            </b-dropdown-item>

            <b-dropdown-item
              :disabled="item.count_files == 0"
              @click="downloadRegistrationFiles(item.id)">
              Download File(s)
            </b-dropdown-item>
            <!-- / Options -->
          </b-dropdown>
        </template>
        <!-- / Registrant Actions -->

        <!-- Created At -->
        <template
          slot="created_at"
          slot-scope="{ item }">
          {{ item.created_at | datetime }}
        </template>
        <!-- / Created At -->

        <!-- Totals -->
        <template
          slot="bottom-row">
          <td colspan="4"><b>Total:</b></td>

          <!-- Total Amount -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Amount:</b>
            {{total(registrations, 'amount')}}
          </td>
          <!-- / Total Amount -->

          <!-- Total Amount Fees -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">{{loggedInUser.actingOrg.name}} Fees:</b>
            {{total(registrations, 'amount_fees')}}
          </td>
          <!-- / Total Amount Fees -->

          <!-- Total Amount Total -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Total:</b>
            {{total(registrations, 'amount_total')}}
          </td>
          <!-- / Total Amount Total -->

          <!-- Total KJK Fees -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">KJK Fees:</b>
            {{total(registrations, 'amount_kjk_fees')}}
          </td>
          <!-- / Total KJK Fees -->

          <!-- Total Collected -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Collected:</b>
            {{total(registrations, 'total_collected')}}
          </td>
          <!-- / Total Collected  -->

          <!-- Total Balance -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Balance:</b>
            {{total(registrations, 'balance_due')}}
          </td>
          <!-- / Total Balance -->
        </template>
        <!-- / Totals -->
      </b-table>
      <!-- / Active Roster -->

      <!-- Waitlist -->
      <h4 class="my-3 d-flex align-items-center text-secondary">
        <span>Waitlist</span>
        <small class="ml-1">({{waitlist.length}})</small>
      </h4>

      <b-table
        striped
        hover
        stacked="lg"
        small
        show-empty
        sort-by="created_at"
        :items="waitlist"
        :fields="registrationFields"
        empty-text="No Waitlisted Registrants to show.">
        <!-- Registrant Actions -->
        <template
          slot="actions"
          slot-scope="{ item }">
          <b-dropdown
            variant="link"
            boundary="window">
            <!-- Hamburger -->
            <template slot="button-content">
              <font-awesome-icon icon="bars"></font-awesome-icon>
            </template>
            <!-- / Hamburger -->

            <!-- Options -->
            <b-dropdown-item
              @click="moveToActiveRoster(item)">
              Move To Active Roster
            </b-dropdown-item>

            <b-dropdown-item
              @click="goToOrder(item.registration_session_uuid)">
              Order Details
            </b-dropdown-item>

            <b-dropdown-item
              @click="goToRegistration(event.id, item.id)">
              Registration Details
            </b-dropdown-item>

            <b-dropdown-item
              @click="resendConfirmationEmail(item.registration_session_uuid)">
              Resend Confirmation Email
            </b-dropdown-item>
            <!-- / Options -->
          </b-dropdown>
        </template>
        <!-- / Registrant Actions -->

        <!-- Created At -->
        <template
          slot="created_at"
          slot-scope="{ item }">
          {{ item.created_at | datetime }}
        </template>
        <!-- / Created At -->

        <!-- Totals -->
        <template
          slot="bottom-row">
          <td colspan="3"><b>Total:</b></td>

          <!-- Total Amount -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Amount:</b>
            {{total(waitlist, 'amount')}}
          </td>
          <!-- / Total Amount -->

          <!-- Total Amount Fees -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">{{loggedInUser.actingOrg.name}} Fees:</b>
            {{total(waitlist, 'amount_fees')}}
          </td>
          <!-- / Total Amount Fees -->

          <!-- Total Amount Total -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Total:</b>
            {{total(waitlist, 'amount_total')}}
          </td>
          <!-- / Total Amount Total -->

          <!-- Total KJK Fees -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">KJK Fees:</b>
            {{total(waitlist, 'amount_kjk_fees')}}
          </td>
          <!-- / Total KJK Fees -->

          <!-- Total Collected -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Collected:</b>
            {{total(waitlist, 'total_collected')}}
          </td>
          <!-- / Total Collected  -->

          <!-- Total Balance -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Balance:</b>
            {{total(waitlist, 'balance_due')}}
          </td>
          <!-- / Total Balance -->
        </template>
        <!-- / Totals -->
      </b-table>
      <!-- / Waitlist -->

      <!-- Canceled -->
      <h4 class="my-3 d-flex align-items-center text-secondary">
        <span>Canceled</span>
        <small class="ml-1">({{canceled.length}})</small>
      </h4>

      <b-table
        striped
        hover
        stacked="lg"
        small
        show-empty
        sort-by="created_at"
        :items="canceled"
        :fields="registrationFields"
        empty-text="No Canceled Registrants to show.">
        <!-- Registrant Actions -->
        <template
          slot="actions"
          slot-scope="{ item }">
          <b-dropdown
            variant="link"
            boundary="window">
            <!-- Hamburger -->
            <template slot="button-content">
              <font-awesome-icon icon="bars"></font-awesome-icon>
            </template>
            <!-- / Hamburger -->

            <!-- Options -->
            <b-dropdown-item
              @click="goToOrder(item.registration_session_uuid)">
              Order Details
            </b-dropdown-item>

            <b-dropdown-item
              @click="goToRegistration(event.id, item.id)">
              Registration Details
            </b-dropdown-item>
            <!-- / Options -->
          </b-dropdown>
        </template>
        <!-- / Registrant Actions -->

        <!-- Created At -->
        <template
          slot="created_at"
          slot-scope="{ item }">
          {{ item.created_at | datetime }}
        </template>
        <!-- / Created At -->

        <!-- Totals -->
        <template
          slot="bottom-row">
          <td colspan="3"><b>Total:</b></td>

          <!-- Total Amount -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Amount:</b>
            {{total(canceled, 'amount')}}
          </td>
          <!-- / Total Amount -->

          <!-- Total Amount Fees -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">{{loggedInUser.actingOrg.name}} Fees:</b>
            {{total(canceled, 'amount_fees')}}
          </td>
          <!-- / Total Amount Fees -->

          <!-- Total Amount Total -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Total:</b>
            {{total(canceled, 'amount_total')}}
          </td>
          <!-- / Total Amount Total -->

          <!-- Total KJK Fees -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">KJK Fees:</b>
            {{total(canceled, 'amount_kjk_fees')}}
          </td>
          <!-- / Total KJK Fees -->

          <!-- Total Collected -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Collected:</b>
            {{total(canceled, 'total_collected')}}
          </td>
          <!-- / Total Collected  -->

          <!-- Total Balance -->
          <td class="text-lg-right sort-padding-diff">
            <b class="d-lg-none">Balance:</b>
            {{total(canceled, 'balance_due')}}
          </td>
          <!-- / Total Balance -->
        </template>
        <!-- / Totals -->
      </b-table>
      <!-- / Canceled -->

      <div class="corner-buttons">
        <!-- Download All Files -->
        <b-button
          class="mr-3 shadow"
          type="button"
          variant="success"
          @click="downloadAllFiles(event.id)"
          :disabled="submitting">
          <font-awesome-icon icon="download"></font-awesome-icon> Download All Active Roster Files
        </b-button>
        <!-- / Download Financials Report -->

        <!-- Download Financials Report -->
        <b-button
          class="mr-3 shadow"
          type="button"
          variant="success"
          @click="downloadFinancialsReport"
          :disabled="submitting">
          <font-awesome-icon icon="dollar-sign"></font-awesome-icon> Download Financials Report
        </b-button>
        <!-- / Download Financials Report -->

        <!-- Create Manual Registration -->
        <b-button
          class="shadow"
          type="button"
          variant="success"
          :disabled="cannotManuallyRegister"
          @click="goToNewRegistrant">
          <font-awesome-icon icon="plus"></font-awesome-icon> Add Registrant
        </b-button>
        <!-- / Create Manual Registration -->
      </div>
    </b-tab>
    <!-- / Registrants -->

    <!-- Confirmation Email -->
    <b-tab
      id="event-registrants"
      class="mt-3 container"
      title="Confirmation Email"
      v-if="event.id">
      <form
        id="confirmation-email-form"
        name="confirmationEmailForm"
        @submit.prevent="submitEvent(event)"
        novalidate>
        <!-- Text -->
        <div class="row">
          <div class="col">
            <label for="event-confirmation-text">Text:</label>
            <b-form-textarea
              id="event-confirmation-text"
              ref="eventConfirmationText"
              name="eventConfirmationText"
              rows="12"
              placeholder="Enter confirmation text"
              v-model="event.confirmation_email_content"
              :readonly="submitting"
              no-resize>
            </b-form-textarea>
          </div>
        </div>
        <!-- / Text -->

        <!-- Attachments -->
        <div class="row mt-3">
          <div class="col">
            <label for="attachments">
              Attached Files:
              <small class="text-danger">
                Total file size must be less than 30MB.
              </small>
            </label>

            <!-- Attachments Table -->
            <b-table
              striped
              hover
              selectable
              small
              show-empty
              select-mode="multiple"
              selected-variant=""
              sort-by="name"
              class="file-attachments"
              @row-clicked="toggleAttachment(event.confirmation_email_file_uuids, $event)"
              :items="fileOptions"
              :fields="attachmentFields"
              empty-text="No attached files.">
              <template
                slot="remove"
                slot-scope="{ item }">
                <b-button
                  type="button"
                  size="sm"
                  variant="link"
                  class="py-0 px-1">
                  <div v-if="isFileAttached(event.confirmation_email_file_uuids, item)">
                    <font-awesome-icon icon="times"></font-awesome-icon>
                    Remove
                  </div>
                  <div v-else>
                    <font-awesome-icon icon="undo"></font-awesome-icon>
                    Undo
                  </div>
                </b-button>
              </template>
              <template
                slot="created"
                slot-scope="{ item }">
                {{ item.created | datetime }}
              </template>
            </b-table>
            <!-- / Attachments Table -->
          </div>
        </div>
        <!-- / Attachments -->

        <!-- Attach New File -->
        <div class="row mt-3">
          <div class="col">
            <label for="attach-new-file">
              Upload File:
              <small class="d-block d-md-inline text-danger">
                You must click "Upload" then "Save" after selecting your file in order to attach it to your message.
              </small>
            </label>
            <b-input-group>
              <b-form-file
                id="attach-new-file"
                ref="attachNewFile"
                name="attachNewFile"
                v-model="attachment"
                placeholder="Choose a file..."
                drop-placeholder="Drop file here..."
                :readonly="submitting">
              </b-form-file>
              <b-input-group-append>
                <b-button
                  @click="uploadFile(event.confirmation_email_file_uuids, 'event_id', event.id)"
                  :disabled="!attachment || uploading"
                  variant="primary">
                  Upload
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </div>
        </div>
        <!-- / Attach New File -->

        <div class="corner-buttons">
          <!-- Save Event Button -->
          <b-button
            class="shadow"
            type="submit"
            variant="success"
            :disabled="submitting">
            <font-awesome-icon icon="save"></font-awesome-icon> Save
          </b-button>
          <!-- / Save Event Button -->
        </div>
      </form>
    </b-tab>
    <!-- / Confirmation Email -->

    <!-- Registration Status Indicator -->
    <b-tab
      id="registration-status"
      :title-link-class="`registration-status text-${eventStatus(event).class}`"
      :title="eventStatus(event).reason"
      v-if="event.id"
      disabled>
    </b-tab>
    <!-- / Registration Status Indicator -->
  </b-tabs>
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import DataSvc from '../services/data';
import AppMixin from '../services/app-mixin';
import DirtyForm from '../services/dirty-form';
import BrowserStorageMixin from '../services/browser-storage-mixin';
import FileUploadMixin from '../services/file-upload-mixin';
import Formatters from '../services/formatters';
import EventBus from '../services/event-bus';

export default {
  name: 'event',
  mixins: [
    DirtyForm,
    AppMixin,
    BrowserStorageMixin,
    FileUploadMixin,
  ],
  data() {
    return {
      // general
      tabIndex: 0,
      submitting: false,

      // event
      eventLoaded: false,
      event: { confirmation_email_file_uuids: [] },
      eventCopy: { confirmation_email_file_uuids: [] },
      roster: {},
      rosterCopy: {},
      eventForm: {
        form_fields: [],
      },

      // registrants
      registrations: [],
      waitlist: [],
      canceled: [],
      registrationFields: [
        {
          key: 'actions',
          label: '',
          class: 'action-column text-center',
        },
        {
          key: 'created_at',
          label: 'Order Placed',
          sortable: true,
        },
        {
          key: 'registrant.full_name',
          label: 'Name',
          sortable: true,
        },
        {
          key: 'amount',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'amount_fees',
          label: `${this.$root.user.actingOrg.name} Fees`,
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'amount_total',
          label: 'Total',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'amount_kjk_fees',
          label: 'KJK Fees',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'total_collected',
          label: 'Collected',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'balance_due',
          label: 'Balance',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
      ],

      // pricing
      pricing: [],
      pricingFields: [
        {
          key: 'label',
          label: 'Name',
          sortable: true,
        },
        {
          key: 'price',
          label: 'Base Price',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'price_fees',
          label: `${this.$root.user.actingOrg.name} Fees`,
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'price_kjk_fees',
          label: 'KJK Fees',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'price_total',
          label: 'Total',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'is_open',
          label: 'Active',
          sortable: true,
          formatter: value => (value ? 'Yes' : 'No'),
        },
      ],

      // promo codes
      promoCodes: [],
      promoCodeFields: [
        {
          key: 'code',
          sortable: true,
        },
        {
          key: 'notes',
          sortable: true,
        },
        {
          key: 'used',
          sortable: true,
          formatter: Formatters.boolToYes,
        },
        {
          key: 'revoked',
          sortable: true,
          formatter: Formatters.boolToYes,
        },
        {
          key: 'valid_start_date_time',
          label: 'Valid From',
          sortable: true,
        },
        {
          key: 'valid_end_date_time',
          label: 'Valid To',
          sortable: true,
        },
        {
          key: 'fees_flat',
          label: `${this.$root.user.actingOrg.name} Fees`,
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'kjk_fees_flat',
          label: 'KJK Fees',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'price',
          label: 'Price',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
        {
          key: 'total',
          label: 'Total',
          sortable: true,
          class: 'text-lg-right',
          tdClass: 'sort-padding-diff',
          formatter: Formatters.currency,
        },
      ],
    };
  },
  watch: {
    $route(newRoute) {
      const { tab } = newRoute.params;
      if (tab) {
        this.tabIndex = this.tabs.indexOf(tab);
      }
    },
  },
  computed: {
    eventDirty() {
      const eventJSON = JSON.stringify(this.event);
      const eventCopyJSON = JSON.stringify(this.eventCopy);
      return eventJSON !== eventCopyJSON;
    },
    rosterDirty() {
      const rosterJSON = JSON.stringify(this.roster);
      const rosterCopyJSON = JSON.stringify(this.rosterCopy);
      return rosterJSON !== rosterCopyJSON;
    },
    anyDirty() {
      return this.eventDirty || this.rosterDirty;
    },
    registrationSiteURL() {
      // eslint-disable-next-line
      let url = `${process.env.VUE_APP_REGISTRATION_SITE_URL}/#/register/event/${this.event.id}`;

      if (this.event.hidden) {
        url += `/${this.event.hidden_code}`;
      }

      return url;
    },
    tabs() {
      return this.hasRole('superadmin')
        ? ['details', 'pricing', 'registrants', 'confirmation-email']
        : ['details', 'registrants', 'confirmation-email'];
    },
    activeRosterFields() {
      const filesField = {
        key: 'count_files',
        label: 'Files',
        class: 'text-lg-right',
        tdClass: 'sort-padding-diff',
        sortable: true,
      };

      const activeRosterFields = _.clone(this.registrationFields);

      activeRosterFields.splice(3, 0, filesField);

      return activeRosterFields;
    },
    cannotManuallyRegister() {
      const openPricing = _.filter(
        this.event.pricing_packages,
        { is_open: true },
      );

      return this.event.event_over || openPricing.length <= 0;
    },
  },
  methods: {
    submitEvent(event) {
      let eventPromise;

      this.submitting = true;

      if (typeof event !== 'object') {
        eventPromise = this.deleteEvent(event);
      } else if (event.id) {
        eventPromise = this.updateEvent(event);
      } else {
        eventPromise = this.createEvent(event);
      }

      eventPromise
        .catch((resp) => {
          if (resp.status !== 400) {
            this.showToast('Something went wrong saving the Event.');
          }
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },
    createEvent(ev) {
      this.showToast('Creating event...');

      const event = _.cloneDeep(ev);
      // event.max_registrations = this.roster.max_registrations;
      // event.max_waitlist = this.roster.max_waitlist;

      return DataSvc.createEvent(this.loggedInUser.actingOrg.id, event)
        .then((e) => {
          e.confirmation_email_file_uuids = [];

          this.event = e;
          this.eventCopy = _.cloneDeep(e);

          let rosterPromise = Promise.resolve();

          // we should get the roster back. If not, we need to tell the user that an error occurred
          if (!this.event.rosters || this.event.rosters.length < 1) {
            console.error('Unable to retrieve Roster from newly created Event. This should not happen.', this.event);
            this.showWarning('Event created, but there was a problem retrieving the Roster. This should not happen.');
            return rosterPromise;
          }
          // We only support one Roster object, so get it from the list that is
          // returned. - EF 2022-02-03
          this.roster = this.event.rosters[0];

          if (this.roster.max_registrations > -1) {
            rosterPromise = DataSvc.getRosters({ event_id: e.id })
              .then((rosters) => {
                this.roster = _.extend(rosters[0], this.roster);
                return DataSvc.updateRoster(this.roster);
              })
              .then((r) => {
                this.roster = r;
                this.rosterCopy = _.cloneDeep(r);
              });
          }

          return rosterPromise;
        })
        .then(() => {
          this.tabIndex = 1;
          this.updateRouteWithTab(22);
          this.$forceUpdate();
          return this.delay(1000);
        })
        .then(() => {
          this.showToast('Event created!');
        });
    },
    copyEvent(ev) {
      ev.max_registrations = this.roster.max_registrations;
      ev.max_waitlist = this.roster.max_waitlist;

      this.submitting = true;

      this.showToast('Copying event...');

      return DataSvc.copyEvent(ev)
        .then((e) => {
          e.confirmation_email_file_uuids = [];

          this.event = e;
          this.eventCopy = _.cloneDeep(e);

          let rosterPromise = Promise.resolve();

          // we should get the roster back. If not, we need to tell the user that an error occurred
          if (!this.event.rosters || this.event.rosters.length < 1) {
            console.error('Unable to retrieve Roster from newly created Event. This should not happen.', this.event);
            this.showWarning('Event created, but there was a problem retrieving the Roster. This should not happen.');
            return rosterPromise;
          }
          // We only support one Roster object, so get it from the list that is
          // returned. - EF 2022-02-03
          this.roster = this.event.rosters[0];

          if (this.roster.max_registrations > -1) {
            rosterPromise = DataSvc.getRosters({ event_id: e.id })
              .then((rosters) => {
                this.roster = _.extend(rosters[0], this.roster);
                return DataSvc.updateRoster(this.roster);
              })
              .then((r) => {
                this.roster = r;
                this.rosterCopy = _.cloneDeep(r);
              });
          }

          return rosterPromise;
        })
        .then(() => this.delay(1000))
        .then(() => {
          this.showToast('Event copied!');

          EventBus.$emit('show-warning', {
            msg: '"' + this.event.name + '" has been created.',
            title: '',
          });

          this.$router.push({
            name: 'event',
            params: {
              eventID: this.event.id,
              tab: 'details',
            },
          });
        })
        .catch((resp) => {
          if (resp.status !== 400) {
            this.showToast('Something went wrong copying the Event.');
          }
        })
        .finally(() => this.delay(1000))
        .finally(() => {
          this.submitting = false;
          this.hideToast();
        });
    },
    updateEvent(event) {
      this.showToast('Updating event...');

      return DataSvc.updateRoster(this.roster)
        .then((r) => {
          this.roster = r;
          this.rosterCopy = _.cloneDeep(r);

          const e = _.cloneDeep(event);

          if (!e.partial_payments_start_date_time) {
            e.partial_payments_start_date_time = null;
          }

          if (!e.partial_payments_end_date_time) {
            e.partial_payments_end_date_time = null;
          }

          return DataSvc.updateEvent(e);
        })
        .then((e) => {
          if (!e.confirmation_email_file_uuids) {
            e.confirmation_email_file_uuids = [];
          }

          this.event = e;
          this.eventCopy = _.cloneDeep(e);

          // set files
          this.fileOptions = _.filter(
            this.fileOptions,
            file => this.event.confirmation_email_file_uuids.includes(file.uuid),
          );

          return Promise.all([
            this.getEventPricing(e.id),
            this.getEventPromoCodes(e.id),
          ]);
        })
        .then(() => this.delay(1000))
        .then(() => {
          this.showToast('Event updated!');
        });
    },
    deleteEvent(eventID) {
      const confirmDelete = () => {
        this.showToast('Deleting event...');

        return DataSvc.deleteEvent(eventID)
          .then(() => this.delay(1000))
          .then(() => {
            this.showToast('Event deleted!');
            this.$router.replace({ name: 'events' });
          });
      };

      return this.showConfirm(
        'Are you sure you want to delete this event?',
        'Deleting an event',
      ).then(confirmDelete);
    },
    goToForm(eventID, formID) {
      this.$router.push({
        name: 'event-form',
        params: { eventID, formID },
      });
    },
    selectPricing(selected) {
      const [p] = selected;
      this.goToPricing(this.event.id, p.id);
    },
    goToNewRegistrant() {
      this.$router.push({
        name: 'new-registrant',
        params: { eventID: this.event.id },
      });
    },
    getEventPricing(eventID) {
      return DataSvc.getEventPricing(eventID)
        .then((pricing) => {
          this.pricing = pricing;
        });
    },
    updateRouteWithTab(eventID) {
      this.$router.push({
        name: 'event',
        params: {
          tab: this.tabs[this.tabIndex],
          eventID,
        },
      });
    },
    goToEventsIfEventNotInSeason(seasonID) {
      // if season selected && it doesn't match current event season, route to events
      if (seasonID && this.event.season_id !== seasonID) {
        this.goToEvents();
      }
    },
    defaultPartialPaymentAmount(ppEnabled) {
      let amount = 0;

      if (ppEnabled) {
        amount = _(this.pricing)
          .orderBy('price_kjk_fees')
          .last().price_kjk_fees;
      }

      this.event.partial_payments_minimum = amount;
    },
    moveToActiveRoster(registration) {
      return DataSvc.moveRegistrationToActiveRoster(registration)
        .then(this.getEventRegistrations);
    },
    getEventRegistrations() {
      return DataSvc.getEventRegistrations(this.event.id)
        .then((registrations) => {
          this.registrations = _(registrations)
            .filter('registered')
            .reject('waitlist')
            .reject('cancelled')
            .value();

          this.waitlist = _(registrations)
            .filter('registered')
            .filter('waitlist')
            .reject('cancelled')
            .value();

          this.canceled = _(registrations)
            .filter('cancelled')
            .value();
        });
    },
    downloadAllFiles(eventID) {
      const download = () => {
        const data = {
          event_id: eventID,
        };

        this.submitting = true;

        return DataSvc.downloadFileArchive(data)
          .then((response) => {
            this.showWarning(
              response.message,
              'Preparing Files',
            );
          })
          .finally(() => {
            this.submitting = false;
          });
      };

      const suppressEventFilesDownloadMessage = this.localStorageGet('kjk.suppressEventFilesDownloadMessage');

      if (!suppressEventFilesDownloadMessage) {
        this.showConfirm(
          `
            Due to the potentially large size of all the combined files for this event,
            you will be sent an email with a link to download them as soon as they are available.<br><br>
            Do you still want to download all files for the Active Roster?<br><br>
          `,
          'Files Delivered Via Email',
          'kjk.suppressEventFilesDownloadMessage',
        ).then(download, () => {});
      } else {
        download();
      }
    },

    // promo codes
    selectPromoCode(selected) {
      const [p] = selected;
      this.goToPromoCode(this.event.id, p.id);
    },
    getEventPromoCodes(eventID) {
      return DataSvc.getEventPromoCodes(eventID)
        .then((promoCodes) => {
          this.promoCodes = _.map(promoCodes, (p) => {
            p.total = p.price + (this.event.fees_included ? 0 : p.fees_flat);
            return p;
          });
        });
    },

    // financials
    total(registrations, property) {
      const sum = _.sumBy(registrations, property);
      return Formatters.currency(sum);
    },
    downloadFinancialsReport() {
      const data = {
        event_ids: [this.event.id],
        cancelled: null,
        waitlist: null,
      };

      const fields = [
        'last_name',
        'first_name',
        'amount',
        'amount_fees',
        'amount_total',
        'amount_kjk_fees',
        'total_collected',
        'balance_due',
        'cancelled',
        'waitlist',
      ];

      const download = (report) => {
        const url = window.URL.createObjectURL(new Blob([report]));
        const link = document.createElement('a');
        const timestamp = moment().format('M-D-YYYY h-mm A');

        link.href = url;
        link.setAttribute('download', `${this.event.name} Financials Report ${timestamp}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      };

      this.showGlobalLoader('Generating report...');
      this.submitting = true;

      return DataSvc.generateReport(
        this.loggedInUser.actingOrg.id,
        fields,
        [],
        data,
        true,
      ).then(download)
        .finally(() => {
          this.hideGlobalLoader();
          this.submitting = false;
        });
    },
  },
  created() {
    this.showGlobalLoader('Loading event...');

    const initEvents = () => {
      const { eventID, tab } = this.$route.params;
      let eventPromise;

      if (this.tabs.includes(tab)) {
        this.tabIndex = this.tabs.indexOf(tab);
      } else {
        this.tabIndex = 0;
        this.updateRouteWithTab(eventID);
      }

      // watch for org/season change and route to events page
      EventBus.$on('acting-org-changed', this.goToEvents);

      // watch for org/season change and route to events page
      EventBus.$on('acting-season-changed', this.goToEventsIfEventNotInSeason);

      if (eventID === 'new') {
        eventPromise = Promise.resolve({});
      } else {
        eventPromise = DataSvc.getEvent(eventID);
      }

      return eventPromise
        .catch(() => {
          this.showWarning('That event does not exist.');
          this.$router.replace({ name: 'events' });
        })
        .then((event) => {
          // set to empty array
          if (!event.confirmation_email_file_uuids) {
            event.confirmation_email_file_uuids = [];
          }

          this.event = event;
          this.eventCopy = _.cloneDeep(event);

          let existingEventPromise = Promise.resolve();

          if (eventID !== 'new') {
            // if event season not in season go back to events
            if (!_.find(this.seasons, { id: event.season_id })) {
              this.$router.replace({ name: 'events' });
            }

            const formsPromise = DataSvc.getForms({ event_id: eventID })
              .then((eventForm) => {
                [this.eventForm] = eventForm;
              });

            const registrationsPromise = this.getEventRegistrations();

            const rosterPromise = DataSvc.getRosters({ event_id: eventID })
              .then((rosters) => {
                [this.roster] = rosters;
                this.rosterCopy = _.cloneDeep(this.roster);
              });

            const filesPromise = DataSvc.getFiles({ event_id: event.id })
              .then((files) => {
                this.fileOptions = _.filter(
                  files,
                  f => event.confirmation_email_file_uuids.includes(f.uuid),
                );
              });

            const pricingPromise = this.getEventPricing(eventID);

            const promoCodePromise = this.getEventPromoCodes(eventID);

            existingEventPromise = Promise.all([
              formsPromise,
              registrationsPromise,
              rosterPromise,
              pricingPromise,
              promoCodePromise,
              filesPromise,
            ]);
          }

          return existingEventPromise;
        })
        .then(() => {
          this.eventLoaded = true;
          // focus first input
          this.$nextTick(() => {
            this.focusInput('eventName');
          });
        });
    };

    this.initPromise.then(initEvents).finally(this.hideGlobalLoader);
  },
  destroyed() {
    EventBus.$off('acting-org-changed', this.goToEvents);
    EventBus.$off('acting-season-changed', this.goToEventsIfEventNotInSeason);
  },
};
</script>

<style lang="scss">
#event {
  position: relative;

  .nav-tabs {
    .nav-item {
      .registration-status {
        font-weight: bold;
        text-transform: uppercase;
      }
    }
  }

  &.new-event {
    .nav-tabs {
      display: none;
    }
  }

  &.submitting {
    .nav-tabs {
      pointer-events: none;
      .nav-link {
        color: #6c757d;
      }
    }
  }

  .event-details-tab {
    max-width: 300px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
}
</style>
